// ** Framework imports
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";

// ** Mui imports
import Box from "@mui/material/Box";
// ** Components imports
import { Navigation } from "@/components/navigation/Navigation.tsx";
import EnumContext from "@/hoc/EnumContext";

// ** Hooks imports
import { usePreferencesState } from "@/store/hooks.ts";

// ** Config imports
import { navigationConfig } from "@/config/navigation.ts";
import { useIsVerticalMode } from "@/hooks/useIsVerticalMode.tsx";
import FlagsContext from "@contexts/FlagsContext.tsx";
import ProgramMessage from "@components/preferences/programs/ProgramMessage.tsx";
import ScrollToTop from "@/components/ScrollToTop";
import { useState } from "react";

export const Route = createFileRoute("/_dashboard")({
  component: PathlessLayoutComponent,
  beforeLoad: ({ context }) => {
    if (!context.session?.isAuthenticated || !context.session.user) {
      throw redirect({
        to: "/login",
      });
    }
  },
});

function PathlessLayoutComponent() {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(
    navigationConfig.mode === "vertical"
  );
  const { contained } = usePreferencesState();
  const isVerticalMode = useIsVerticalMode({});
  const session = Route.useRouteContext().session;
  const actualDrawerWidth = isDrawerOpen
    ? navigationConfig.drawerWidth
    : navigationConfig.closedDrawerWidth;

  if (!session) return null;

  return (
    <EnumContext>
      <FlagsContext>
        <Box
          sx={(theme) => ({
            bgcolor: theme.palette.background.default,
            minHeight: "100vh",
            height: "100%",
            ...(isVerticalMode && {
              display: "flex",
              flexDirection: "row",
            }),
          })}
        >
          <Navigation
            isDrawerOpen={isDrawerOpen}
            setIsDrawerOpen={setIsDrawerOpen}
            session={session}
            actualDrawerWidth={actualDrawerWidth}
          />
          <Box
            component="main"
            sx={{
              p: 2,
              maxWidth: contained ? "xl" : "unset",
              height: "100%",
              width: `calc(100% - ${actualDrawerWidth}px)`,
              mx: "auto",
            }}
          >
            <ProgramMessage session={session} />
            <Outlet />
          </Box>
        </Box>
        <ScrollToTop />
      </FlagsContext>
    </EnumContext>
  );
}
